import { defaultBlockRegistry } from './block-registry.gen.js';
import { resolveSymbolName, SYMBOL_TOKEN } from '../blocks/_shared/materialSymbols.js';
/** Field ids declared `icon-name` on a fields list (the canonical icon-glyph inputs). */
function iconNameFieldIds(fields) {
    return (fields ?? []).filter((f) => f.type === 'icon-name').map((f) => f.id);
}
/**
 * Section types → their fixed chrome glyphs (`schema.staticIcons`): icons a section ALWAYS renders from
 * markup (e.g. insta-stories' opener/nav symbols), not via an `icon-name` field. Any such section present
 * in the document contributes these to the subset unconditionally. Cached per section registry.
 */
const staticIconCache = new WeakMap();
function staticIconIndex(sectionRegistry) {
    const cached = staticIconCache.get(sectionRegistry);
    if (cached)
        return cached;
    const index = new Map();
    for (const [type, def] of Object.entries(sectionRegistry)) {
        if (def.schema.staticIcons?.length)
            index.set(type, def.schema.staticIcons);
    }
    staticIconCache.set(sectionRegistry, index);
    return index;
}
/**
 * Map every node TYPE (section or block) → its `icon-name` field ids, from BOTH registries: the global
 * block registry (blocks referenced via `accepts`, at any depth) and each section schema's own fields +
 * inline `blocks` (legacy in-section child schemas, e.g. icons-with-content's `icon`). Cached per section
 * registry — the default registries are stable, so the index builds once for the storefront.
 */
const indexCache = new WeakMap();
function iconFieldIndex(sectionRegistry, blockRegistry) {
    const cached = indexCache.get(sectionRegistry);
    if (cached)
        return cached;
    const index = new Map();
    // UNION, not first-wins: the SAME child `type` (e.g. `column`) is declared by more than one section
    // schema — `custom-columns`' column has no icon field, `icon-bar`'s column has an `icon-name` field.
    // First-writer-wins would let the icon-less declarer claim the type and drop the other's glyphs from
    // the shipped subset (they'd render as raw ligature text on the live storefront). Merge every
    // declarer's icon-name field ids so no glyph is ever missed. Over-collection is safe (a spare KB);
    // a miss is not. Ensure the type is present (empty list) even when no declarer has an icon field.
    const put = (type, fields) => {
        const existing = index.get(type);
        if (!existing) {
            index.set(type, iconNameFieldIds(fields));
            return;
        }
        for (const id of iconNameFieldIds(fields))
            if (!existing.includes(id))
                existing.push(id);
    };
    for (const [type, def] of Object.entries(blockRegistry))
        put(type, def.schema.fields);
    for (const [type, def] of Object.entries(sectionRegistry)) {
        put(type, def.schema.fields);
        for (const child of def.schema.blocks ?? [])
            put(child.type, child.fields);
    }
    indexCache.set(sectionRegistry, index);
    return index;
}
/** Keep a stored value only if it resolves to a real glyph token (snake_case word, not an emoji/color). */
function pushGlyph(value, acc) {
    const name = resolveSymbolName(typeof value === 'string' ? value : undefined);
    if (SYMBOL_TOKEN.test(name) && /[a-z]/.test(name))
        acc.add(name);
}
// Bound the recursion so a malformed (cyclic) `blocks` graph can never overflow the stack — Shrine's
// deepest real nesting (main-product → Gallery → blocks → collapsible-row → children) is ~5 levels.
const MAX_BLOCK_DEPTH = 32;
function walkBlocks(blocks, index, acc, depth) {
    if (!Array.isArray(blocks) || depth > MAX_BLOCK_DEPTH)
        return;
    for (const block of blocks) {
        if (!block || typeof block !== 'object')
            continue;
        const ids = index.get(block.type);
        if (ids)
            for (const id of ids)
                pushGlyph(block.settings?.[id], acc);
        walkBlocks(block.blocks, index, acc, depth + 1);
    }
}
/**
 * The de-duplicated glyph names this document renders. Pass the SAME `sectionRegistry` PageRenderer uses;
 * the global block registry is shared (override only for tests). Empty when the document draws no icons.
 *
 * NEVER throws: this runs at the very top of PageRenderer (before the section error boundaries), so a
 * single malformed node — a null block, a non-array `blocks`, a cyclic graph — must degrade to "no subset"
 * (the full icon font) rather than crash the whole page render. That brittleness is exactly the Shopyan
 * renderer failure mode (CLAUDE.md) we must make impossible by construction, not rely on clean input for.
 */
export function collectIconNames(document, sectionRegistry, blockRegistry = defaultBlockRegistry) {
    const acc = new Set();
    try {
        const index = iconFieldIndex(sectionRegistry, blockRegistry);
        const staticIcons = staticIconIndex(sectionRegistry);
        const sections = document?.sections;
        if (Array.isArray(sections)) {
            for (const section of sections) {
                if (!section || typeof section !== 'object')
                    continue;
                const ids = index.get(section.type);
                if (ids)
                    for (const id of ids)
                        pushGlyph(section.settings?.[id], acc);
                const fixed = staticIcons.get(section.type);
                if (fixed)
                    for (const name of fixed)
                        pushGlyph(name, acc);
                walkBlocks(section.blocks, index, acc, 0);
            }
        }
    }
    catch {
        // A malformed document must never crash the render — fall back to the full icon font.
    }
    return [...acc];
}
//# sourceMappingURL=collectIconNames.js.map